import { FormikProps } from "formik";
import React from "react";
import { Form, Col } from "react-bootstrap";
import Select from "react-select";
import {
    PublishStatus,
    BaseButton,
    Error,
    RadioInput,
    TextAreaInput,
    TextInput,
    FileWithPreview,
    ICategoryType,
    IGeneralFormValues,
    IPlanType,
    IMemberPlan,
    selectTheme,
} from "../../../../libs";

import * as Styled from "./GeneralUploadForm.styled";
import { ThumbnailImagesSection } from "../../upload-modal-body";

interface IProps {
    formik: FormikProps<IGeneralFormValues>;
    handleThumbnailImageUpload: (thumbnailImage: FileWithPreview) => void;
    handleThumbnailDelete: () => void;
    percentage: number;
    categories: ICategoryType[];
    plans: IMemberPlan[];
    abortHandler?: () => void;
}

export const GeneralUploadForm: React.FC<IProps> = ({
    formik,
    handleThumbnailImageUpload,
    handleThumbnailDelete,
    percentage,
    categories,
    plans,
    abortHandler,
}) => {
    const [formattedCategories, setFormattedCategories] = React.useState<
        { value: string; label: string }[]
    >([]);

    const [formattedPlanOptions, setFormattedPlanOptions] = React.useState<
        { label: string; value: string }[]
    >([]);

    React.useEffect(() => {
        setFormattedCategories(
            categories.map((category) => ({
                value: category.categoryId,
                label: category.categoryType,
            })),
        );
        try {
            setFormattedPlanOptions(
                plans
                    .filter(
                        (el) =>
                            (el.isEnabled &&
                                el.isPublic &&
                                el.active &&
                                (el.planType === IPlanType.VOD ||
                                    formik.values.planId?.includes(el.id))) || formik.values.planId?.includes(el.id) // If a non VOD plan is already selected, then show it in the dropdown
                    )
                    .map((item: IMemberPlan) => ({
                        value: item.id,
                        label: `${item.name} -  $${item.amount
                            .toFixed(2)
                            .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")}`,
                    })),
            );
        } catch (err) {
            console.log(err);
        }
    }, [categories, formik.values.planId, plans]);

    return (
        <Form onSubmit={formik.handleSubmit}>
            <Styled.RequiredText>* indicates required</Styled.RequiredText>
            <Styled.FormFieldRow>
                <Col>
                    <TextInput
                        fieldName="title"
                        fieldLabel="Title *"
                        placeholder="Add a title for the video"
                        fieldValue={formik.values.title}
                        onChangeHandler={formik.handleChange}
                        onBlurHandler={formik.handleBlur}
                        hasError={formik.touched.title && !!formik.errors.title}
                    />
                    {formik.touched.title && formik.errors.title && (
                        <Error message={formik.errors.title} />
                    )}
                </Col>
            </Styled.FormFieldRow>
            <Styled.FormFieldRow>
                <Col>
                    <TextAreaInput
                        fieldName="description"
                        fieldLabel="Description"
                        placeholder="Tell viewers what the video is about"
                        fieldValue={formik.values.description}
                        onChangeHandler={formik.handleChange}
                        onBlurHandler={formik.handleBlur}
                    />
                </Col>
            </Styled.FormFieldRow>
            <Styled.ThumbnailSectionContainer>
                <Styled.FormFieldRow>
                    <Col>
                        <Styled.ThumbnailHeader>
                            Thumbnail Images
                        </Styled.ThumbnailHeader>
                    </Col>
                </Styled.FormFieldRow>
                <Styled.FormFieldRow>
                    <Styled.ThumbnailText>
                        Upload an image that reflects the content of the video.
                        A good thumbnail stands out and attracts the attention
                        of users.
                    </Styled.ThumbnailText>
                </Styled.FormFieldRow>
                <ThumbnailImagesSection
                    onThumbnailDelete={handleThumbnailDelete}
                    onThumbnailUpload={handleThumbnailImageUpload}
                />
            </Styled.ThumbnailSectionContainer>
            <Styled.FormFieldRow>
                <Col>
                    <Styled.ThumbnailHeader>Category *</Styled.ThumbnailHeader>
                </Col>
            </Styled.FormFieldRow>
            <Styled.FormFieldRow>
                <Col>
                    <Select
                        className="custom-select"
                        isMulti
                        styles={selectTheme.multiSelect(!!formik.errors.categories)}
                        placeholder="Select Category"
                        value={formattedCategories.filter((category) =>
                            formik.values.categories.includes(category.value),
                        )}
                        isClearable
                        options={formattedCategories}
                        onChange={(selectedOptions) => {
                            formik.setFieldValue(
                                "categories",
                                selectedOptions.map((option) => option.value),
                            );
                        }}
                    />
                </Col>
            </Styled.FormFieldRow>
            <Styled.FormFieldRow>
                <Col>
                    <Styled.ThumbnailHeader>Visibility</Styled.ThumbnailHeader>
                </Col>
            </Styled.FormFieldRow>
            <Styled.FormFieldRow>
                <Col>
                    <Styled.VisibilityOptionsWrapper
                        direction="vertical"
                        gap={3}
                    >
                        <RadioInput
                            fieldLabel="Draft (Save it for later)"
                            fieldName="status"
                            fieldId="DRAFT"
                            checked={
                                formik.values.status === PublishStatus.DRAFT
                            }
                            fieldValue={PublishStatus.DRAFT}
                            onChangeHandler={formik.handleChange}
                        />
                        <RadioInput
                            fieldValue={PublishStatus.PUBLISHED}
                            fieldLabel="Public (Anyone with the video link can watch you video)"
                            fieldName="status"
                            fieldId="PUBLIC"
                            checked={
                                formik.values.status === PublishStatus.PUBLISHED
                            }
                            onChangeHandler={formik.handleChange}
                        />
                        <RadioInput
                            fieldValue={PublishStatus.SCHEDULED}
                            fieldLabel="Schedule (Select a date to make your video public)"
                            fieldName="status"
                            fieldId="SCHEDULED"
                            checked={
                                formik.values.status === PublishStatus.SCHEDULED
                            }
                            onChangeHandler={formik.handleChange}
                        />
                    </Styled.VisibilityOptionsWrapper>
                </Col>
            </Styled.FormFieldRow>
            <Styled.FormFieldRow>
                <Col>
                    <Styled.ThumbnailHeader>Link Plan</Styled.ThumbnailHeader>
                </Col>
            </Styled.FormFieldRow>
            <Styled.FormFieldRow>
                <Col>
                    <Select
                        className="custom-select"
                        isMulti
                        styles={selectTheme.multiSelectWithValidation(!!formik.errors.planId, plans)}
                        placeholder="Select Plans"
                        value={formattedPlanOptions.filter((plan) =>
                            formik.values.planId.includes(plan.value),
                        )}
                        isClearable
                        options={formattedPlanOptions}
                        onChange={(selectedOptions) => {
                            formik.setFieldValue(
                                "planId",
                                selectedOptions.map((option) => option.value),
                            );
                        }}
                    />
                </Col>
            </Styled.FormFieldRow>
            <Styled.ActionButtonsWrapper>
                {abortHandler && (
                    <BaseButton
                        type="button"
                        variant={formik.isSubmitting ? "error" : "secondary"}
                        onClick={() => {
                            abortHandler();
                        }}
                        disabled={!formik.isSubmitting}
                    >
                        Abort Upload
                    </BaseButton>
                )}
                <BaseButton
                    type="submit"
                    variant={!formik.isValid ? "secondary" : "primary"}
                    disabled={!formik.isValid}
                >
                    {formik.isSubmitting && (
                        <>
                            <Styled.LoadingSpinner
                                animation="border"
                                size="sm"
                                variant="primary"
                            />
                        </>
                    )}
                    {formik.isSubmitting
                        ? `${percentage}% Uploaded`
                        : percentage === 100
                            ? "Processing"
                            : "Submit"}
                </BaseButton>
            </Styled.ActionButtonsWrapper>
        </Form>
    );
};
